<script setup lang="ts">
import { computed } from "vue";
import { useRouter } from "vue-router";

import { useSidebarBackground } from "@/common/composables/useSidebarBackground";
import { WHITE_MWS_LOGO_URL } from "@/common/utils/constants";

import NavigationContent from "./NavigationContent.vue";
import BaseIcon from "../icons/BaseIcon.vue";

defineProps<{ isDesktopSidebarOpen: boolean }>();

defineEmits<{
  (e: "update:isDesktopSidebarOpen", value: boolean): void;
}>();

const router = useRouter();
const { sidebarBackgroundClass } = useSidebarBackground();

const envSuffix = computed(() => {
  switch (import.meta.env.VITE_MODE) {
    case "acceptance":
      return "acc";
    case "staging":
      return "staging";
    case "local":
      return "local";
    default:
      return "";
  }
});
</script>

<template>
  <div
    class="hidden md:flex md:flex-col md:fixed md:inset-y-0 z-40"
    :class="{
      'md:w-60': isDesktopSidebarOpen,
      'md:w-14': !isDesktopSidebarOpen,
    }"
  >
    <div
      class="flex-1 flex flex-col min-h-0"
      :class="sidebarBackgroundClass"
    >
      <div class="flex items-center p-4 pb-2">
        <div class="flex flex-col items-center">
          <img
            v-show="isDesktopSidebarOpen"
            class="h-8 w-[90px] cursor-pointer"
            :src="WHITE_MWS_LOGO_URL"
            alt="MWS"
            @click="router.push('/')"
          />
          <p
            v-show="isDesktopSidebarOpen"
            class="text-white font-bold -mb-1"
          >
            {{ envSuffix }}
          </p>
        </div>

        <button
          v-show="isDesktopSidebarOpen"
          class="absolute right-0"
          @click="$emit('update:isDesktopSidebarOpen', false)"
        >
          <BaseIcon
            name="heroicons:chevron-double-left"
            class="w-5 mr-3 text-white"
          />
        </button>
        <button
          v-show="!isDesktopSidebarOpen"
          @click="$emit('update:isDesktopSidebarOpen', true)"
        >
          <BaseIcon
            name="heroicons:bars-3-bottom-left"
            class="size-6 text-white"
          />
        </button>
      </div>

      <div class="flex-1 flex flex-col overflow-y-auto hide-scrollbar">
        <nav class="flex-1 py-2 space-y-1">
          <NavigationContent v-if="isDesktopSidebarOpen === true" />
        </nav>
      </div>
    </div>
  </div>
</template>

<style scoped lang="postcss">
.hide-scrollbar {
  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */

  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }
}
</style>
